import { PurchaseAgentDrawer } from '@components/PurchaseAgentDrawer';
import { PurchaseAgentTable } from '@components/PurchaseAgentTable';
import { SearchAgentTable } from '@components/SearchAgentTable';
import { Button, Grid, Link as MuiLink, makeStyles, SvgIcon, Typography } from '@material-ui/core';
import { RouteComponentProps } from '@reach/router';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as PurchaseAgentIcon } from 'src/assets/icons/purchase-agent.svg';
import PageContainer from 'src/components/PageContainer';
import { useFeatureFlags } from 'src/hooks/useFeatureFlags';
import { useSearchAgents } from 'src/hooks/useSearchAgents';
import { UserPermissions, useUserPermissions } from 'src/hooks/useUserPermissions';

const PURCHASE_AGENTS_ANCHOR = 'kaufagenten';
const SEARCH_AGENTS_ANCHOR = 'suchagenten';

const useStyles = makeStyles((theme) => ({
  pageContainer: {
    paddingBlockEnd: theme.spacing(6),
  },
  pageHeaderRow: {
    marginBlockEnd: theme.spacing(4.5),
  },
  pageTitle: {
    textDecorationLine: 'underline',
    textDecorationColor: theme.palette.intelligence.main,
    textDecorationThickness: theme.spacing(0.25),
  },
  searchAgentTable: {
    marginBlockEnd: theme.spacing(8),
  },
  sectionTitle: {
    fontWeight: 'bold',
    marginBlockEnd: theme.spacing(2),
  },
  purchaseAgentTitle: {
    fontWeight: 'bold',
    marginBlockEnd: theme.spacing(2),
    textDecorationLine: 'underline',
    textDecorationColor: theme.palette.intelligence.main,
  },
  purchaseAgentTable: {
    marginBlockEnd: theme.spacing(4),
  },
  jumpLink: {
    color: theme.palette.intelligence.main,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  createButton: {
    backgroundColor: theme.palette.intelligence.main,
    color: theme.palette.common.white,
    '&:hover': {
      backgroundColor: theme.palette.intelligence.dark,
    },
  },
}));

const SearchAgentsPage = (_props: RouteComponentProps): JSX.Element => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [isPADrawerOpen, setPADrawerOpen] = useState(false);
  const [paInitialSearchAgentId, setPAInitialSearchAgentId] = useState<string>();
  const [editPurchaseAgentId, setEditPurchaseAgentId] = useState<string>();

  const handleCreatePurchaseAgent = useCallback((searchAgentId: string) => {
    setEditPurchaseAgentId(undefined);
    setPAInitialSearchAgentId(searchAgentId);
    setPADrawerOpen(true);
  }, []);

  const handleEditPurchaseAgent = (id: string) => {
    setPAInitialSearchAgentId(undefined);
    setEditPurchaseAgentId(id);
    setPADrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setPADrawerOpen(false);
    setPAInitialSearchAgentId(undefined);
    setEditPurchaseAgentId(undefined);
  };

  const { isSuccess: searchAgentsLoaded } = useSearchAgents();
  const purchaseAgentFlagEnabled = useFeatureFlags('purchaseAgent');
  const hasPurchaseAgentPermission = useUserPermissions(UserPermissions.PURCHASE_AGENT);
  const showPurchaseAgent = purchaseAgentFlagEnabled && hasPurchaseAgentPermission;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const createPA = params.get('createPA');
    if (createPA) {
      handleCreatePurchaseAgent(createPA);
      window.history.replaceState(null, '', window.location.pathname + window.location.hash);
    } else if (!window.location.hash) {
      window.scrollTo(0, 0);
    }
  }, [handleCreatePurchaseAgent]);

  useEffect(() => {
    if (window.location.hash && searchAgentsLoaded) {
      document.getElementById(window.location.hash.slice(1))?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [searchAgentsLoaded]);

  return (
    <PageContainer className={classes.pageContainer}>
      <Grid
        id={SEARCH_AGENTS_ANCHOR}
        container
        justifyContent="space-between"
        alignItems="flex-start"
        className={classes.pageHeaderRow}
      >
        <Typography variant="h1" className={classes.pageTitle}>
          {t('searchAgentsPage.title')}
        </Typography>
        {showPurchaseAgent && (
          <MuiLink href={`#${PURCHASE_AGENTS_ANCHOR}`} className={classes.jumpLink}>
            {t('searchAgentsPage.jumpToPurchaseAgents')}
            <SvgIcon component={PurchaseAgentIcon as any} viewBox="0 0 32 32" fontSize="small" />
          </MuiLink>
        )}
      </Grid>

      <Typography variant="h2" className={classes.sectionTitle}>
        {t('searchAgentsPage.searchAgentsTitle')}
      </Typography>
      <Typography gutterBottom>{t('searchAgentsPage.subheader')}</Typography>
      <SearchAgentTable
        className={classes.searchAgentTable}
        onCreatePurchaseAgent={showPurchaseAgent ? handleCreatePurchaseAgent : undefined}
      />

      {showPurchaseAgent && (
        <>
          <Grid
            id={PURCHASE_AGENTS_ANCHOR}
            container
            justifyContent="space-between"
            alignItems="center"
            style={{ marginBlockEnd: 16 }}
          >
            <Typography variant="h2" className={classes.purchaseAgentTitle}>
              {t('purchaseAgent.page.title')}
            </Typography>
            <Button
              variant="contained"
              className={classes.createButton}
              onClick={() => {
                setEditPurchaseAgentId(undefined);
                setPADrawerOpen(true);
              }}
            >
              {t('purchaseAgent.drawer.title')}
            </Button>
          </Grid>
          <Typography gutterBottom>{t('purchaseAgent.page.subheader')}</Typography>
          <PurchaseAgentTable className={classes.purchaseAgentTable} onEditPurchaseAgent={handleEditPurchaseAgent} />

          <PurchaseAgentDrawer
            open={isPADrawerOpen}
            onClose={handleDrawerClose}
            initialSearchAgentId={paInitialSearchAgentId}
            editPurchaseAgentId={editPurchaseAgentId}
          />
        </>
      )}
    </PageContainer>
  );
};

export default SearchAgentsPage;
